import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Role } from '../../types/identity';
import { useAuth } from '../../contexts/AuthContext';
interface Props {
  /** When given, the identity must hold one of these roles. */
  roles?: Role[];
  children?: React.ReactNode;
}

/**
 * The route guard. It captures where the person was heading so they land back
 * there after signing in, rather than being dropped on a generic dashboard.
 */
export function RequireAuth({
  roles,
  children
}: Props) {
  const {
    authenticated,
    restoring,
    identity,
    setReturnTo
  } = useAuth();
  const location = useLocation();
  const destination = `${location.pathname}${location.search}`;
  useEffect(() => {
    if (!restoring && !authenticated && destination !== '/') {
      setReturnTo(destination);
    }
  }, [restoring, authenticated, destination, setReturnTo]);

  /* Nothing is decided until the stored session has been read, otherwise a
     signed-in person would be bounced to sign-in on every refresh. */
  if (restoring) {
    return <div className="flex min-h-screen items-center justify-center bg-canvas px-4" data-id="element-465" data-mp="1tq3wthyii84t">
        <p className="text-body text-ink-muted" role="status" data-id="element-466" data-mp="1xrfmfnhdytd0">
          Restoring your session…
        </p>
      </div>;
  }
  if (!authenticated) {
    return <Navigate to="/welcome" replace data-id="element-467" data-mp="1orsm750e7tlj" />;
  }
  if (roles && identity && !roles.some(role => identity.roles.includes(role))) {
    return <Navigate to="/no-access" replace data-id="element-468" data-mp="1a2nyj8atwx61" />;
  }
  return children ? <>{children}</> : <Outlet data-id="element-469" data-mp="1evh39w6as5i4" />;
}